/**
 * PATH: apps/quote/src/lib/setupApi.ts
 * PURPOSE: Initialize the Quote Hub API client with unified auth + org context.
 *
 * Convergence C4-MR2: the hub previously built a private client with
 * hand-rolled "robust" token getters (legacy localStorage fallbacks from
 * the pre-cookie SSO era) and suppressed the org header behind a STALE
 * CORS note — the backend's CORS_ALLOW_HEADERS has allowed
 * `x-organisation-id` for a long time (verified by live preflight against
 * quotech-backend.quotech.ai). It now boots the canonical @quotech/api
 * setupHubApi() like every other hub: cookie → store token resolution,
 * X-Organisation-ID header, identity-drift guard, and 401 → master-login
 * redirect all come from ONE shared client.
 *
 * ORG CONTRACT: quotebuilder views scope by the `org_id` QUERY PARAM
 * first (quotebuilder/utils/org.py) — callers keep sending it via
 * withOrgConfig() / getOrganisationIdForRequests(). The header the shared
 * client adds is the backend's documented fallback, and it un-breaks the
 * header-only views (master-quotes / payment-claims querysets).
 */

import type { AxiosInstance } from 'axios'
import { setupHubApi, getConfiguredClient } from '@quotech/api'
import { getOrgCookie, useAuthStore } from '@quotech/auth'

let configured = false

/**
 * Initialize the API client for the Quote module.
 * Call this once at app startup (in main.tsx).
 */
export function setupQuoteApi(): AxiosInstance {
  setupHubApi({ hubName: 'Quote' })
  configured = true
  return getConfiguredClient()
}

/** The shared auth-wired axios client (self-initializes if needed). */
export function getApiClient(): AxiosInstance {
  if (!configured) return setupQuoteApi()
  return getConfiguredClient()
}

/**
 * Hook-style API accessor for use in React components.
 * Returns the configured API client instance.
 */
export function useApi(): AxiosInstance {
  return getApiClient()
}

/**
 * Organisation id for the `org_id` query param the quotebuilder backend
 * scopes by (see withOrgConfig()). Cookie → store → legacy localStorage,
 * with the historical dev default of '1' preserved as last resort —
 * backend validate_org_access still gates actual access.
 */
export function getOrganisationIdForRequests(): string {
  const cookieOrg = getOrgCookie()?.id
  const storeOrg = useAuthStore.getState().organisationId
  const legacyOrg = typeof window !== 'undefined' ? localStorage.getItem('CurrentTenantId') : null
  return cookieOrg || storeOrg || legacyOrg || '1'
}
